import {
  DateSelectValue,
  DateSelector,
  Popover,
  PopoverContent,
  PopoverHeading,
} from "@certn/cascadia-react";
import {
  formatDateSelectValueAsISODate,
  parseISODateAsDateSelectValue,
  validateDateSegments,
} from "@certn/cascadia-react/components/inputs/date-input/utils";
import { formatDate } from "@certn/utils";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { SummaryItem } from "../components/SummaryItem";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

export function DateSelectControl(props: CascadiaControlProps) {
  const [date, setDate] = useState<DateSelectValue>();
  const { t } = useTranslation();

  const options = props.uischema.options || {};

  // convert string ISO date to date object d/m/y
  useEffect(() => {
    // JSONForms data can init as undefined instead of ""
    if (!props.data) return;

    const newValue = parseISODateAsDateSelectValue(props.data);
    if (newValue) {
      setDate(newValue);
    }
  }, [props.data]);

  // convert date object d/m/y to string ISO date for JSONForms and save date object to state
  const handleOnChange = (value: DateSelectValue) => {
    setDate(value);

    const isoDate = formatDateSelectValueAsISODate(value);
    props.setValue(isoDate);
  };

  // builds custom error message bypassing external validation
  // component is composed of multiple input fields but is considered one field
  const partialDateErrorMessage = validateDateSegments(
    t,
    date,
    props.label,
    options.granularity
  );

  const formattedDate = formatDate(props.data, "PP", "");

  const { isSummaryForPath, formID, isSubmitted } = useContext(
    CascadiaJsonFormContext
  );

  if (isSummaryForPath?.(props.path)) {
    return <SummaryItem label={props.label}>{formattedDate}</SummaryItem>;
  }

  const showErrorsBeforeSubmit = options.showErrorsBeforeSubmit !== false;

  const getErrors = () => {
    if (showErrorsBeforeSubmit || isSubmitted) {
      if (props.errors?.length) {
        return props.errors;
      }
      if (
        props.touched &&
        (date || props.required) && [partialDateErrorMessage]
      ) {
        return [partialDateErrorMessage];
      }
    }
    return [];
  };

  const errors = getErrors();

  return (
    <DateSelector
      errors={errors}
      granularity={options.granularity}
      roundUp={options.roundUp}
      maxDate={
        options.allowFutureDate || options.allowPastAndFutureDates
          ? undefined
          : new Date()
      }
      isFutureDate={options.allowFutureDate}
      allowPastAndFutureDates={options.allowPastAndFutureDates}
      isDisabled={!props.enabled}
      isInvalid={errors.length > 0 && props.invalid}
      isRequired={props.required}
      label={props.label}
      name={props.path}
      onBlur={props.setTouched}
      onChange={handleOnChange}
      value={date}
      testID={
        formID
          ? `${formID}-${props.path}-date-field`
          : `${props.path}-date-field`
      }
      showFormattedDate={options.showFormattedDate}
      popoverLabel={options.popoverLabel}
      popover={
        options.popoverLabel && (
          <Popover placement={options.popoverPlacement || "top"}>
            {options.popoverHeading && (
              <PopoverHeading>{options.popoverHeading}</PopoverHeading>
            )}
            {options.popoverContent && (
              <PopoverContent>{options.popoverContent}</PopoverContent>
            )}
          </Popover>
        )
      }
    />
  );
}

DateSelectControl.displayName = "DateSelectControl";

export const DateSelectRenderer = withCascadiaControlProps(DateSelectControl);
